import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FormControlLabel, Switch } from '@mui/material';
import styles from './PeriodPicker.module.css';
import { COMPARE_MODES, MAX_PERIOD_DAYS, NO_COMPARE, PERIOD_PRESETS, customPeriod, presetPeriod } from '../utils/periods';

const fieldSx = {
  width: 160,
  '& .MuiOutlinedInput-root': { height: '40px', borderRadius: 'var(--border-radius-sm)' },
};

// Espera a pessoa terminar de digitar a data antes de recarregar as seções
const APPLY_DELAY_MS = 500;

/**
 * Seletor do período: atalhos em um clique (semana, mês, ano) e, sempre à
 * vista, as datas "De" e "Até" com calendário. Mexer numa data vira período
 * personalizado (nenhum atalho fica marcado). A comparação começa desligada;
 * ligada, escolhe entre o período anterior e o mesmo período do ano passado
 * (a última escolha volta quando religa).
 *
 * `value`: { key, start, end, compare }; `onChange(period)`.
 */
const PeriodPicker = ({ value, onChange }) => {
  const [from, setFrom] = useState(dayjs(value.start));
  const [to, setTo] = useState(dayjs(value.end));
  const comparing = value.compare && value.compare !== NO_COMPARE;
  const [lastCompare, setLastCompare] = useState(comparing ? value.compare : COMPARE_MODES[0].key);

  const invalid = !from || !to || !from.isValid() || !to.isValid() || to.isBefore(from, 'day');
  const tooLong = !invalid && to.diff(from, 'day') + 1 > MAX_PERIOD_DAYS;

  // Data digitada ou escolhida no calendário: aplica sozinha, sem botão
  useEffect(() => {
    if (invalid || tooLong) return undefined;
    const next = customPeriod(from, to, value.compare);
    if (next.start === value.start && next.end === value.end) return undefined;
    const timer = setTimeout(() => onChange(next), APPLY_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, invalid, tooLong]);

  function pickPreset(key) {
    const next = presetPeriod(key, value.compare);
    setFrom(dayjs(next.start));
    setTo(dayjs(next.end));
    onChange(next);
  }

  function toggleCompare(on) {
    onChange({ ...value, compare: on ? lastCompare : NO_COMPARE });
  }

  function pickCompare(mode) {
    setLastCompare(mode);
    onChange({ ...value, compare: mode });
  }

  return (
    <div className={styles.picker}>
      <div className={styles.chips} role="group" aria-label="Período">
        {PERIOD_PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            className={`${styles.chip} ${value.key === preset.key ? styles.chipActive : ''}`}
            onClick={() => pickPreset(preset.key)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <div className={styles.custom}>
          <DatePicker
            label="De"
            value={from}
            onChange={setFrom}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: fieldSx } }}
          />
          <DatePicker
            label="Até"
            value={to}
            onChange={setTo}
            format="DD/MM/YYYY"
            minDate={from && from.isValid() ? from : undefined}
            slotProps={{ textField: { size: 'small', sx: fieldSx } }}
          />
          {tooLong && <span className={styles.warning}>Escolha até 2 anos de cada vez.</span>}
        </div>
      </LocalizationProvider>

      <div className={styles.compare}>
        <FormControlLabel
          control={<Switch size="small" checked={comparing} onChange={(event) => toggleCompare(event.target.checked)} />}
          label={comparing ? 'Comparar com' : 'Comparar com outro período'}
          className={styles.compareSwitch}
        />
        {comparing && (
          <div className={styles.segment} role="group" aria-label="Comparar com">
            {COMPARE_MODES.map((mode) => (
              <button
                key={mode.key}
                type="button"
                className={`${styles.segmentItem} ${value.compare === mode.key ? styles.segmentActive : ''}`}
                aria-pressed={value.compare === mode.key}
                onClick={() => pickCompare(mode.key)}
              >
                {mode.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PeriodPicker;
